import React, { useState } from "react";
import Modal from "../Common/Modal";
import SecurityIcon from "../Icons/SecurityIcon";

// Default password - can be changed or made configurable
const AUTH_PASSWORD = "admin@765265";

/**
 * Password authentication modal for sensitive operations
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onAuthenticated: () => void
 *   title: string (optional)
 *   description: string (optional)
 */
const PasswordAuthModal = ({ 
  open, 
  onClose, 
  onAuthenticated, 
  title = "Authentication Required",
  description = "Enter your password to proceed with this action." 
}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Simulate a brief delay for authentication
    await new Promise(resolve => setTimeout(resolve, 500));

    if (password === AUTH_PASSWORD) {
      setPassword("");
      setError("");
      setIsLoading(false);
      onAuthenticated();
      onClose();
    } else {
      setError("Incorrect password. Please try again.");
      setPassword("");
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  if (!open) return null;

  return (
    <Modal onClose={handleClose}>
      <div className="p-6 max-w-md mx-auto">
        <div className="text-center mb-4">
          <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
            <SecurityIcon className="w-6 h-6 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 text-sm mt-2">{description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="auth-password"
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
              autoFocus
            />
            {error && (
              <div className="text-sm text-red-600 mt-1">{error}</div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium disabled:opacity-50"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!password.trim() || isLoading}
            >
              {isLoading ? "Authenticating..." : "Authenticate"}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-500 text-center">
            This action requires authentication for security purposes.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PasswordAuthModal;
